import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { QR_COLUMN_KEYS, QR_COLUMN_LABELS, type QRColumnKey } from "../constants";
import type { ReportPrefs, FilterCondition } from "../../../../../hooks/useReportPreferences";
import CustomDropdown from "../../../../../components/CustomDropdown/CustomDropdown";
import CollapsibleSection from "../../../../../components/CollapsibleSection/CollapsibleSection";

interface Props {
  open: boolean;
  onClose: () => void;
  prefs: ReportPrefs;
  onSave: (p: ReportPrefs) => void;
  onReset: () => void;
}

type Tab = "data" | "visual";
const CHK = "accent-teal-dark";

const FILTER_BY_OPTIONS = [
  { value: "distributionAccount", label: "Distribution account" },
  { value: "type", label: "Transaction type" },
  { value: "refNo", label: "#" },
  { value: "payee", label: "Name" },
  { value: "description", label: "Description" },
  { value: "accountName", label: "Account" },
  { value: "cleared", label: "Cleared" },
  { value: "amount", label: "Amount" },
  { value: "date", label: "Line created date" },
  { value: "modifiedDate", label: "Line modified date" },
  { value: "serviceDate", label: "Service date" },
  { value: "accountType", label: "Account type" },
  { value: "dueDate", label: "Line due date" },
  { value: "supplierName", label: "Supplier name" },
  { value: "paymentMethod", label: "Payment method" },
  { value: "productService", label: "Product/Service" },
  { value: "employeeName", label: "Employee name" },
  { value: "arPaid", label: "A/R paid" },
  { value: "apPaid", label: "A/P paid" },
  { value: "chequePrinted", label: "Cheque printed" },
  { value: "terms", label: "Terms" },
];

const CONDITION_OPTIONS = [
  { value: "equals", label: "equals" },
  { value: "not-equals", label: "not equals" },
  { value: "contains", label: "contains" },
  { value: "is-empty", label: "is empty" },
  { value: "is-not-empty", label: "is not empty" },
];

const NEGATIVE_OPTIONS = [
  { value: "minus", label: "-100" },
  { value: "paren", label: "(100)" },
];

const BORDER_OPTIONS = [
  { value: "default", label: "Default" },
  { value: "none", label: "No borders" },
  { value: "horizontal", label: "Horizontal borders" },
  { value: "vertical", label: "Vertical borders" },
  { value: "inner", label: "Inner borders" },
  { value: "outer", label: "Outer borders" },
  { value: "horizontal-outer", label: "Horizontal and outer borders" },
  { value: "vertical-outer", label: "Vertical and outer borders" },
];

export default function CustomizeDrawer({ open, onClose, prefs, onSave, onReset }: Props) {
  const [tab, setTab] = useState<Tab>("data");
  const [draft, setDraft] = useState<ReportPrefs>(prefs);

  useEffect(() => { if (open) setDraft(prefs); }, [open, prefs]);

  if (!open) return null;

  const upd = <K extends keyof ReportPrefs>(key: K, val: ReportPrefs[K]) => setDraft((d) => ({ ...d, [key]: val }));

  const toggleCol = (key: QRColumnKey) => setDraft((d) => ({
    ...d,
    hiddenColumns: d.hiddenColumns.includes(key) ? d.hiddenColumns.filter((k) => k !== key) : [...d.hiddenColumns, key],
  }));

  const toggleRightAlign = (key: QRColumnKey) => setDraft((d) => ({
    ...d,
    columnRightAlign: { ...d.columnRightAlign, [key]: !d.columnRightAlign[key] },
  }));

  const needsValue = draft.filterCondition !== "is-empty" && draft.filterCondition !== "is-not-empty";

  const handleApply = () => { onSave(draft); onClose(); };

  return createPortal(
    <div className="fixed inset-0 z-[8000]" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-black/20" onClick={onClose} />
      <div className="absolute top-0 right-0 bottom-0 w-[440px] bg-white shadow-2xl flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#EEF1F4]">
          <h3 className="text-[17px] font-bold text-ink">Customize</h3>
          <button type="button" onClick={onClose} aria-label="Close" className="text-ink3 hover:text-ink"><X className="w-6 h-6" /></button>
        </div>
        <div className="flex mx-5 mt-3.5 bg-[#F7F9FB] rounded-[10px] p-1">
          <button type="button" onClick={() => setTab("data")} className={`flex-1 py-2.5 text-[13px] font-semibold rounded-lg ${tab === "data" ? "bg-white text-ink shadow-sm" : "text-ink2"}`}>Data</button>
          <button type="button" onClick={() => setTab("visual")} className={`flex-1 py-2.5 text-[13px] font-semibold rounded-lg ${tab === "visual" ? "bg-white text-ink shadow-sm" : "text-ink2"}`}>Visual</button>
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-2">
          {tab === "data" ? (
            <>
              <CollapsibleSection title="Filters">
                <MetaRow sub="Select your data filtering method" link="Clear all" onLink={() => { upd("filterValue", ""); upd("filterCondition", "equals"); }} />
                <FieldLabel>Filter by</FieldLabel>
                <CustomDropdown value={draft.filterBy} options={FILTER_BY_OPTIONS} onChange={(v) => upd("filterBy", v)} searchable />
                <FieldLabel>Condition</FieldLabel>
                <CustomDropdown value={draft.filterCondition} options={CONDITION_OPTIONS} onChange={(v) => upd("filterCondition", v as FilterCondition)} />
                {needsValue && (
                  <>
                    <FieldLabel>Value</FieldLabel>
                    <input type="text" value={draft.filterValue} onChange={(e) => upd("filterValue", e.target.value)} placeholder="Enter the text" className="w-full h-[38px] border border-[#D8DEE6] rounded-lg px-3.5 text-[13px] bg-white focus:outline-none focus:border-teal-dark" />
                  </>
                )}
                <button type="button" className="block mt-3.5 text-[13px] text-teal-dark font-semibold" onClick={() => window.alert("Multiple conditions coming in follow-up.")}>+ Add condition</button>
                <button type="button" className="block mt-2 text-[13px] text-teal-dark font-semibold" onClick={() => window.alert("Multiple filters coming in follow-up.")}>+ Add another filter</button>
              </CollapsibleSection>

              <CollapsibleSection title="Groups">
                <MetaRow sub="Choose how to group your data" />
                <FieldLabel>Level 1</FieldLabel>
                <div className="h-[38px] border border-[#D8DEE6] rounded-lg px-3.5 flex items-center text-[13px] text-ink bg-white">Distribution account</div>
              </CollapsibleSection>

              <CollapsibleSection title="Columns">
                <MetaRow sub="Toggle columns to show or hide." link="Reset" onLink={() => upd("hiddenColumns", [])} />
                {QR_COLUMN_KEYS.map((key) => (
                  <label key={key} className="flex items-center gap-2.5 py-1.5 text-[13px] cursor-pointer">
                    <input type="checkbox" checked={!draft.hiddenColumns.includes(key)} onChange={() => toggleCol(key)} className={`${CHK} w-4 h-4`} />
                    {QR_COLUMN_LABELS[key]}
                  </label>
                ))}
              </CollapsibleSection>

              <CollapsibleSection title="Number format">
                <MetaRow sub="Choose how to format your data" />
                <label className="flex items-center gap-2.5 py-1.5 text-[13px] cursor-pointer"><input type="checkbox" checked={draft.divideBy1000} onChange={(e) => upd("divideBy1000", e.target.checked)} className={`${CHK} w-4 h-4`} /> Divide by 1000</label>
                <label className="flex items-center gap-2.5 py-1.5 text-[13px] cursor-pointer"><input type="checkbox" checked={draft.hideCurrency} onChange={(e) => upd("hideCurrency", e.target.checked)} className={`${CHK} w-4 h-4`} /> Don't show currency symbol</label>
                <FieldLabel>Negative numbers</FieldLabel>
                <CustomDropdown value={draft.negativeFormat} options={NEGATIVE_OPTIONS} onChange={(v) => upd("negativeFormat", v as any)} />
                <label className="flex items-center gap-2.5 py-2 text-[13px] cursor-pointer"><input type="checkbox" checked={draft.negativeInRed} onChange={(e) => upd("negativeInRed", e.target.checked)} className={`${CHK} w-4 h-4`} /> Show in red</label>
                <FieldLabel>Decimals</FieldLabel>
                <label className="flex items-center gap-2.5 py-1.5 text-[13px] cursor-pointer"><input type="radio" name="dec" checked={draft.decimals === 0} onChange={() => upd("decimals", 0)} className={`${CHK} w-4 h-4`} /> Round to the nearest whole number</label>
                <label className="flex items-center gap-2.5 py-1.5 text-[13px] cursor-pointer"><input type="radio" name="dec" checked={draft.decimals === 2} onChange={() => upd("decimals", 2)} className={`${CHK} w-4 h-4`} /> Show decimals up to 2 places</label>
              </CollapsibleSection>
            </>
          ) : (
            <>
              <CollapsibleSection title="Header">
                <label className="flex items-center gap-2.5 py-1.5 text-[13px] cursor-pointer"><input type="checkbox" checked={draft.showCompanyLogo} onChange={(e) => upd("showCompanyLogo", e.target.checked)} className={`${CHK} w-4 h-4`} /> Company logo</label>
                <label className="flex items-center gap-2.5 py-1.5 text-[13px] cursor-pointer"><input type="checkbox" checked={draft.showCompanyName} onChange={(e) => upd("showCompanyName", e.target.checked)} className={`${CHK} w-4 h-4`} /> Company name</label>
                <label className="flex items-center gap-2.5 py-1.5 text-[13px] cursor-pointer"><input type="checkbox" checked={draft.showReportTitle} onChange={(e) => upd("showReportTitle", e.target.checked)} className={`${CHK} w-4 h-4`} /> Report title</label>
                <label className="flex items-center gap-2.5 py-1.5 text-[13px] cursor-pointer"><input type="checkbox" checked={draft.showReportPeriod} onChange={(e) => upd("showReportPeriod", e.target.checked)} className={`${CHK} w-4 h-4`} /> Report period</label>
                <FieldLabel>Header layout</FieldLabel>
                <label className="flex items-center gap-2.5 py-1.5 text-[13px] cursor-pointer"><input type="radio" name="hlay" checked={draft.headerLayout === "name-first"} onChange={() => upd("headerLayout", "name-first")} className={`${CHK} w-4 h-4`} /> Company name first</label>
                <label className="flex items-center gap-2.5 py-1.5 text-[13px] cursor-pointer"><input type="radio" name="hlay" checked={draft.headerLayout === "title-first"} onChange={() => upd("headerLayout", "title-first")} className={`${CHK} w-4 h-4`} /> Report title first</label>
                <FieldLabel>Alignment</FieldLabel>
                <div className="flex gap-5 py-1">
                  {(["left", "center", "right"] as const).map((a) => (
                    <label key={a} className="flex items-center gap-1.5 text-[13px] cursor-pointer capitalize">
                      <input type="radio" name="halign" checked={draft.headerAlignment === a} onChange={() => upd("headerAlignment", a)} className={`${CHK} w-4 h-4`} /> {a === "center" ? "Centre" : a}
                    </label>
                  ))}
                </div>
              </CollapsibleSection>

              <CollapsibleSection title="Footer">
                <label className="flex items-center gap-2.5 py-1.5 text-[13px] cursor-pointer"><input type="checkbox" checked={draft.showDatePrepared} onChange={(e) => upd("showDatePrepared", e.target.checked)} className={`${CHK} w-4 h-4`} /> Date prepared</label>
                <label className="flex items-center gap-2.5 py-1.5 text-[13px] cursor-pointer"><input type="checkbox" checked={draft.showTimePrepared} onChange={(e) => upd("showTimePrepared", e.target.checked)} className={`${CHK} w-4 h-4`} /> Time prepared</label>
                <FieldLabel>Alignment</FieldLabel>
                <div className="flex gap-5 py-1">
                  {(["left", "center", "right"] as const).map((a) => (
                    <label key={a} className="flex items-center gap-1.5 text-[13px] cursor-pointer capitalize">
                      <input type="radio" name="falign" checked={draft.footerAlignment === a} onChange={() => upd("footerAlignment", a)} className={`${CHK} w-4 h-4`} /> {a === "center" ? "Centre" : a}
                    </label>
                  ))}
                </div>
                <FieldLabel>Show empty cells as</FieldLabel>
                <div className="flex gap-5 py-1">
                  <label className="flex items-center gap-1.5 text-[13px] cursor-pointer"><input type="radio" name="empty" checked={draft.emptyCellDisplay === "dash"} onChange={() => upd("emptyCellDisplay", "dash")} className={`${CHK} w-4 h-4`} /> Dash</label>
                  <label className="flex items-center gap-1.5 text-[13px] cursor-pointer"><input type="radio" name="empty" checked={draft.emptyCellDisplay === "blank"} onChange={() => upd("emptyCellDisplay", "blank")} className={`${CHK} w-4 h-4`} /> Blank</label>
                </div>
              </CollapsibleSection>

              <CollapsibleSection title="Column settings">
                <MetaRow sub="Right-align each column individually." />
                {QR_COLUMN_KEYS.map((key) => (
                  <div key={key} className="flex justify-between items-center py-1.5">
                    <span className="text-[13px]">{QR_COLUMN_LABELS[key]}</span>
                    <input type="checkbox" checked={!!draft.columnRightAlign[key]} onChange={() => toggleRightAlign(key)} className={`${CHK} w-4 h-4`} />
                  </div>
                ))}
              </CollapsibleSection>

              <CollapsibleSection title="Row settings">
                <label className="flex items-center gap-2.5 py-1.5 text-[13px] cursor-pointer"><input type="checkbox" checked={draft.bandedRowColour} onChange={(e) => upd("bandedRowColour", e.target.checked)} className={`${CHK} w-4 h-4`} /> Banded row colour</label>
              </CollapsibleSection>

              <CollapsibleSection title="Gridlines">
                <FieldLabel>Report borders</FieldLabel>
                <CustomDropdown value={draft.borders} options={BORDER_OPTIONS} onChange={(v) => upd("borders", v as any)} />
              </CollapsibleSection>

              <CollapsibleSection title="Data">
                <MetaRow sub="Transaction detail reports" />
                <label className="flex items-center gap-2.5 py-1.5 text-[13px] cursor-pointer"><input type="checkbox" checked={draft.displayDebitCredit} onChange={(e) => upd("displayDebitCredit", e.target.checked)} className={`${CHK} w-4 h-4`} /> Display debit/credit column on transaction reports</label>
              </CollapsibleSection>
            </>
          )}
          <div className="pt-4 pb-6">
            <button type="button" onClick={() => { onReset(); onClose(); }} className="text-[13px] text-teal-dark font-semibold">Reset all to defaults</button>
          </div>
        </div>
        <div className="flex justify-end gap-3 px-5 py-3.5 border-t border-[#EEF1F4] bg-[#F7F9FB]">
          <button type="button" onClick={onClose} className="h-9 px-4 border border-[#D8DEE6] rounded-[10px] text-[13px] font-semibold text-ink bg-white">Cancel</button>
          <button type="button" onClick={handleApply} className="h-9 px-4 border border-ink rounded-[10px] text-[13px] font-semibold text-white bg-ink">Apply changes</button>
        </div>
      </div>
    </div>,
    document.body
  );
}

function MetaRow({ sub, link, onLink }: { sub: string; link?: string; onLink?: () => void }) {
  return (
    <div className="flex justify-between items-center mb-2.5">
      <div className="text-[12.5px] text-ink3">{sub}</div>
      {link && <button type="button" onClick={onLink} className="text-[12.5px] text-teal-dark font-semibold">{link}</button>}
    </div>
  );
}

function FieldLabel({ children }: { children: React.ReactNode }) {
  return <div className="text-[13px] text-ink mt-3 mb-1.5">{children}</div>;
}